import React, { useEffect, useState } from "react";
import "../mobile.css";

/**
 * A PDF from the API, offered from a tap: it is fetched first (the API key rides on fetch, not on a
 * plain link), so ONE tap opens the phone's share sheet (Print = AirPrint, Save to Files, AirDrop):
 * iPhone only lets a page share inside the tap itself, never after a download. Where files can't be
 * shared (a desktop browser in phone preview) it opens in a new tab instead. Layout only.
 *
 *   <PdfButton url={`${API_BASE}/invoices/12/pdf`} filename="Invoice_1012.pdf" label="Invoice #1012" />
 *
 * `fetchNow` = fetch on mount (a document the person was just handed, e.g. a manifest after a
 * ship); otherwise the first tap fetches it and the second shares it.
 */
export default function PdfButton({ url, filename, label, fetchNow = false, className = "btn btn-outline-primary w-100 text-start", testId = "pdf-button" }) {
  const [state, setState] = useState({ status: fetchNow ? "loading" : "idle", file: null, href: null, error: null });
  const [want, setWant] = useState(fetchNow ? 1 : 0);
  const [shareError, setShareError] = useState(null);

  useEffect(() => {
    if (!want) return undefined;
    let alive = true;
    let href = null;
    setState({ status: "loading", file: null, href: null, error: null });
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`The PDF did not load (HTTP ${res.status}).`);
        return res.blob();
      })
      .then((blob) => {
        if (!alive) return;
        href = URL.createObjectURL(blob);
        const file = new File([blob], filename || "document.pdf", { type: "application/pdf" });
        setState({ status: "ready", file, href, error: null });
      })
      .catch((err) => {
        if (alive) setState({ status: "failed", file: null, href: null, error: (err && err.message) || "The PDF did not load." });
      });
    return () => {
      alive = false;
      if (href) URL.revokeObjectURL(href);
    };
  }, [url, filename, want]);

  const canShare = Boolean(
    state.file && typeof navigator !== "undefined" && navigator.canShare && navigator.share && navigator.canShare({ files: [state.file] })
  );

  async function share() {
    setShareError(null);
    try {
      await navigator.share({ files: [state.file], title: label });
    } catch (err) {
      if (!err || err.name !== "AbortError") setShareError("The share sheet didn't open. Use Open instead.");
    }
  }

  const icon = <i className="fas fa-file-pdf me-2 text-danger" aria-hidden="true" />;
  return (
    <div className="mb-2" data-testid={testId}>
      {state.status === "idle" && (
        <button type="button" className={className} onClick={() => setWant((w) => w + 1)}>
          {icon}{label}
        </button>
      )}
      {state.status === "loading" && (
        <button type="button" className={className} disabled>
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />{label}…
        </button>
      )}
      {state.status === "ready" && canShare && !shareError && (
        <button type="button" className={className} onClick={share}>
          <i className="fas fa-share-square me-2" aria-hidden="true" />{label}: print or share
        </button>
      )}
      {state.status === "ready" && (!canShare || shareError) && (
        <a className={className} href={state.href} target="_blank" rel="noopener noreferrer">
          {icon}Open {label}
        </a>
      )}
      {shareError && <div className="text-danger small mt-1">{shareError}</div>}
      {state.status === "failed" && (
        <>
          <div className="text-danger small mb-1">{label}: {state.error}</div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setWant((w) => w + 1)}>Try again</button>
        </>
      )}
    </div>
  );
}
